/**
 * Admin API client for the AI model registry (`ai_models`).
 *
 * Capability fields are nullable by design: null means "unknown", which is
 * the correct state for every model Claude Code already knows. See
 * docs/plans/2026-09-17-ai-gateway-p3-design.md §4.2.
 */

import { z } from 'zod';
import { isAxiosError } from 'axios';
import { api } from './api';

export const AiModelSchema = z.object({
  id: z.string(),
  model_id: z.string(),
  upstream: z.enum(['vertex', 'local', 'bedrock']),
  api: z.enum(['anthropic', 'gemini']).default('anthropic'),
  upstream_model_id: z.string(),
  provider: z.string(),
  region: z.string().nullable(),
  enabled: z.boolean(),
  context_window: z.number().nullable(),
  max_output_tokens: z.number().nullable(),
  supports_prompt_caching: z.boolean().nullable(),
  supports_extended_thinking: z.boolean().nullable(),
});

export const GatewayStatusSchema = z.object({
  enabled: z.boolean(),
  routable_count: z.number(),
  unroutable_reason: z.string().nullable(),
});

const RateSchema = z.coerce.number();

export const EffectivePriceSchema = z.object({
  model_id: z.string(),
  provider: z.string(),
  source: z.enum(['override', 'contract', 'effective', 'catalog', 'bundled']),
  input_per_mtok: RateSchema,
  output_per_mtok: RateSchema,
  cache_read_per_mtok: RateSchema.nullable().optional(),
  cache_write_per_mtok: RateSchema.nullable().optional(),
  price_version: z.string(),
  effective_from: z.string().nullable().optional(),
});

export type EffectivePrice = z.infer<typeof EffectivePriceSchema>;

/** USD per 1M tokens. */
export interface PriceOverrideBody {
  input_per_mtok: number;
  output_per_mtok: number;
  cache_read_per_mtok: number;
  cache_write_per_mtok?: number;
  effective_from?: string;
}

export type AiModel = z.infer<typeof AiModelSchema>;
export type GatewayStatus = z.infer<typeof GatewayStatusSchema>;
export type AiModelCreate = Omit<AiModel, 'id'> & Partial<Pick<AiModel, 'id'>>;

export async function listAiModels(): Promise<AiModel[]> {
  const response = await api.get('/admin/ai-models');
  return z.array(AiModelSchema).parse(response.data);
}

export async function createAiModel(body: Partial<AiModelCreate>): Promise<AiModel> {
  const response = await api.post('/admin/ai-models', body);
  return AiModelSchema.parse(response.data);
}

export async function updateAiModel(
  modelId: string,
  body: Partial<AiModelCreate>,
): Promise<AiModel> {
  const response = await api.patch(`/admin/ai-models/${modelId}`, body);
  return AiModelSchema.parse(response.data);
}

export async function deleteAiModel(modelId: string): Promise<void> {
  await api.delete(`/admin/ai-models/${modelId}`);
}

export async function getGatewayStatus(): Promise<GatewayStatus> {
  const response = await api.get('/admin/ai-gateway/status');
  return GatewayStatusSchema.parse(response.data);
}

/** The backend answers 404 when a model has no price at all; that is `null`, not an error. */
export async function getAiModelPrice(modelId: string): Promise<EffectivePrice | null> {
  try {
    const response = await api.get(`/admin/ai-models/${modelId}/price`);
    return EffectivePriceSchema.parse(response.data);
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
}

export async function setAiModelPrice(
  modelId: string,
  body: PriceOverrideBody,
): Promise<EffectivePrice> {
  const response = await api.put(`/admin/ai-models/${modelId}/price`, body);
  return EffectivePriceSchema.parse(response.data);
}

export async function clearAiModelPrice(modelId: string): Promise<void> {
  await api.delete(`/admin/ai-models/${modelId}/price`);
}
